import * as React from "react";
import { useContext } from "react";
import { DropDownList, DropDownListChangeEvent } from "@progress/kendo-react-dropdowns";
import { DropDownListType } from "./DropDownListType";
import getNextLtlRoute from "./GetNextLtlRoute/GetNextLtlRoute";
import { EditProjectContext } from "../../../../EditProjectContext";
import { LtlPipelineViewModel } from "../../../../../ViewModel/LtlPipelineViewModel";
import { LtlRoutingRouteViewModel } from "../../../../../ViewModel/LtlRoutingRouteViewModel";
import LoadingPanel from "../../../../../../Layout/LoadingPanel";
import { isLtlPipelineSpdr } from "./IsLtlPipelineSpdr";
import { ProjectViewModel } from "../../../../../ViewModel/ProjectViewModel";
import { apiErrorNotificationToast } from "../../../../../../Errors/apiErrorNotificationToast";
import { projectUrls } from "../../../../../../URL/ApplicationUrls";
import { AxiosError } from "axios";
import { MdmApiError } from "../../../../../../Errors/MdmApiError";

const getContainerNetwork = (project: ProjectViewModel, pdsrId: number) =>
  project.PdsrsOnThisProject?.find((x) => x.PdsrId === pdsrId)?.ContainerNetwork;

export default function RouteDropDownList({
  ltlPipeline,
  onValueChanged,
}: {
  readonly ltlPipeline: LtlPipelineViewModel;
  readonly onValueChanged: (value: LtlRoutingRouteViewModel) => void;
}) {
  const editProjectContext = useContext(EditProjectContext);
  const { project } = editProjectContext;

  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [routes, setRoutes] = React.useState<Array<LtlRoutingRouteViewModel>>(
    [] as Array<LtlRoutingRouteViewModel>
  );

  function loadData() {
    setIsLoading(true);

    const getNextLtlRouteForSpdrOrPdsr = isLtlPipelineSpdr(ltlPipeline)
      ? getNextLtlRoute(project, ltlPipeline, projectUrls.nextLtlRouteForSpdr(project.ProjectId))
      : getNextLtlRoute(
          project,
          {
            ...ltlPipeline,
            ContainerNetwork: getContainerNetwork(project, ltlPipeline.PdsrId || -1),
          },
          projectUrls.nextLtlRouteForPdsr(project.ProjectId)
        );

    getNextLtlRouteForSpdrOrPdsr
      .then(setRoutes)
      .catch((error: AxiosError<MdmApiError>) => apiErrorNotificationToast(error))
      .finally(() => setIsLoading(false));
  }

  if (isLoading) {
    return (
      <div
        style={{
          position: "relative",
          height: "50%",
        }}
      >
        <LoadingPanel isLoading={isLoading} />
      </div>
    );
  }

  if (routes.length === 0) {
    return (
      <DropDownList
        style={{ width: "100%" }}
        data={[]}
        textField="text"
        dataItemKey="id"
        onOpen={() => loadData()}
      />
    );
  }

  const getDropDownValues = (): Array<DropDownListType<string>> =>
    routes.map<DropDownListType<string>>((r: LtlRoutingRouteViewModel) => ({
      value: r?.RouteName || "RouteName undefined",
      text: r?.RouteDisplayName || "RouteDisplayName undefined",
    }));

  const handleChange = (event: DropDownListChangeEvent) => {
    const value = event.target.value as DropDownListType<string>;
    const displayName = value.text;

    // We are using the DisplayName here, because the RouteName is not unique when a Route is used
    // in active (A) and modified (N) variants, which happens when the FTL route is modified in this project
    const routeViewModel = routes.find((x) => x.RouteDisplayName === displayName);

    if (!routeViewModel) {
      console.warn(
        `handleChange was unable to find route in routes with routeName ${displayName}`,
        {
          routeName: displayName,
          routes,
        }
      );
      return;
    }

    onValueChanged(routeViewModel);
    setRoutes([]);
  };

  return (
    <DropDownList
      style={{ width: "100%" }}
      data={getDropDownValues()}
      textField="text"
      dataItemKey="id"
      onChange={handleChange}
      opened={routes.length !== 0}
    />
  );
}
